import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import '../styles/ConfigEditor.css';
import RelationshipGraph from '../components/RelationshipGraph';
import FieldTypePicker from '../components/FieldTypePicker';

import { useAuth } from '../components/AuthContext';
import { useState as useStateReact } from 'react';
const API_BASE = import.meta.env.VITE_API_BASE;

/* ── tiny SVG helpers (matching Data Viewer) ── */
const SvgSave = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 1h6.5L10 2.5V10a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="4" y="6" width="4" height="3" rx=".5" stroke="currentColor" strokeWidth="1"/>
    <path d="M4 1v2.5h3V1" stroke="currentColor" strokeWidth="1"/>
  </svg>
);
const SvgPlus = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path d="M5.5 2v7M2 5.5h7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);
const SvgTrash = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path d="M2 3h7M4 3V2h3v1M3.5 3v6a.5.5 0 00.5.5h3a.5.5 0 00.5-.5V3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
  </svg>
);
const SvgCopy = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <rect x="3.5" y="3.5" width="6" height="7" rx="1" stroke="currentColor" strokeWidth="1.1"/>
    <path d="M8.5 3.5V2.5a1 1 0 00-1-1h-5a1 1 0 00-1 1v5a1 1 0 001 1h1" stroke="currentColor" strokeWidth="1.1"/>
  </svg>
);
const SvgNew = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <rect x="2" y="1" width="8" height="10" rx="1" stroke="currentColor" strokeWidth="1.1"/>
    <path d="M6 4v4M4 6h4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
  </svg>
);
const SvgPencil = () => (
  <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
    <path d="M8.5 1.5l2 2L4 10H2v-2z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DOT_COLORS = ['#6366f1','#0f766e','#b45309','#475569','#9ca3af','#e11d48','#0284c7','#7c3aed'];

const FIELD_TYPES = [
  // ── Identity ──
  { value: 'uuid',           label: 'UUID',             group: 'Identity' },
  { value: 'int',            label: 'Integer',          group: 'Identity' },
  { value: 'auto_increment', label: 'Auto Increment',   group: 'Identity' },

  // ── Text ──
  { value: 'string',   label: 'String',           group: 'Text' },
  { value: 'text',     label: 'Text (paragraph)', group: 'Text' },
  { value: 'sentence', label: 'Sentence',         group: 'Text' },
  { value: 'word',     label: 'Word',             group: 'Text' },
  { value: 'slug',     label: 'Slug',             group: 'Text' },

  // ── Numbers ──
  { value: 'float',      label: 'Float',              group: 'Numbers' },
  { value: 'decimal',    label: 'Decimal (currency)', group: 'Numbers' },
  { value: 'percentage', label: 'Percentage',         group: 'Numbers' },

  // ── Person ──
  { value: 'name',        label: 'Full Name',  group: 'Person' },
  { value: 'first_name',  label: 'First Name', group: 'Person' },
  { value: 'last_name',   label: 'Last Name',  group: 'Person' },
  { value: 'username',    label: 'Username',   group: 'Person' },
  { value: 'email',       label: 'Email',      group: 'Person' },
  { value: 'phone',       label: 'Phone',      group: 'Person' },
  { value: 'age',         label: 'Age',        group: 'Person' },
  { value: 'gender',      label: 'Gender',     group: 'Person' },
  { value: 'job_title',   label: 'Job Title',  group: 'Person' },
  { value: 'ssn',         label: 'SSN',        group: 'Person' },
  { value: 'date_of_birth', label: 'Date of Birth', group: 'Person' },

  // ── Business ──
  { value: 'company_name',  label: 'Company Name',     group: 'Business' },
  { value: 'product_name',  label: 'Product Name',     group: 'Business' },
  { value: 'store_name',    label: 'Store Name',       group: 'Business' },
  { value: 'brand_name',    label: 'Brand Name',       group: 'Business' },
  { value: 'department',    label: 'Department',       group: 'Business' },
  { value: 'industry',      label: 'Industry',         group: 'Business' },
  { value: 'sku',           label: 'SKU / Product Code', group: 'Business' },
  { value: 'price',         label: 'Price',            group: 'Business' },
  { value: 'currency_code', label: 'Currency Code',    group: 'Business' },
  { value: 'credit_card',   label: 'Credit Card #',    group: 'Business' },
  { value: 'iban',          label: 'IBAN',             group: 'Business' },

  // ── Healthcare ──
  { value: 'hospital_name', label: 'Hospital Name',    group: 'Healthcare' },
  { value: 'drug_name',     label: 'Drug / Medicine',  group: 'Healthcare' },
  { value: 'diagnosis',     label: 'Diagnosis',        group: 'Healthcare' },
  { value: 'blood_type',    label: 'Blood Type',       group: 'Healthcare' },

  // ── Location ──
  { value: 'address',   label: 'Full Address',       group: 'Location' },
  { value: 'street',    label: 'Street',             group: 'Location' },
  { value: 'city',      label: 'City',               group: 'Location' },
  { value: 'state',     label: 'State / Province',   group: 'Location' },
  { value: 'country',   label: 'Country',            group: 'Location' },
  { value: 'zip_code',  label: 'ZIP / Postal Code',  group: 'Location' },
  { value: 'latitude',  label: 'Latitude',           group: 'Location' },
  { value: 'longitude', label: 'Longitude',          group: 'Location' },

  // ── Date & Time ──
  { value: 'timestamp', label: 'Timestamp',    group: 'Date & Time' },
  { value: 'date',      label: 'Date',         group: 'Date & Time' },
  { value: 'time',      label: 'Time',         group: 'Date & Time' },
  { value: 'year',      label: 'Year',         group: 'Date & Time' },

  // ── Internet ──
  { value: 'url',         label: 'URL',            group: 'Internet' },
  { value: 'domain',      label: 'Domain Name',    group: 'Internet' },
  { value: 'ipv4',        label: 'IPv4 Address',   group: 'Internet' },
  { value: 'ipv6',        label: 'IPv6 Address',   group: 'Internet' },
  { value: 'mac_address', label: 'MAC Address',    group: 'Internet' },
  { value: 'user_agent',  label: 'User Agent',     group: 'Internet' },

  // ── Other ──
  { value: 'boolean',   label: 'Boolean',      group: 'Other' },
  { value: 'color',     label: 'Color',        group: 'Other' },
  { value: 'hex_color', label: 'Hex Color',    group: 'Other' },
  { value: 'file_name', label: 'File Name',    group: 'Other' },
  { value: 'mime_type', label: 'MIME Type',    group: 'Other' },
  { value: 'json',      label: 'JSON Object',  group: 'Other' },
];

const SAMPLE_VALUES = {
  string: 'Hello', int: '42', float: '3.14', email: 'john@example.com',
  uuid: '550e8400…', timestamp: '2025-04-06T14:30', phone: '+1-202-555-0173',
  name: 'John Smith', boolean: 'true', address: '123 Main St, NY',
  first_name: 'Alice', last_name: 'Johnson', username: 'alice_j',
  company_name: 'Acme Corp', product_name: 'Widget Pro', store_name: 'MegaMart',
  hospital_name: 'City General', city: 'New York', country: 'US',
  price: '$29.99', url: 'https://…', date: '2025-04-06', age: '34',
  job_title: 'Engineer', brand_name: 'Nike', department: 'Sales',
  drug_name: 'Amoxicillin', blood_type: 'O+', sku: 'SKU-49281',
  auto_increment: '1, 2, 3…', text: 'Lorem ipsum…', decimal: '149.99',
  percentage: '73%', gender: 'Female', diagnosis: 'Hypertension',
  credit_card: '4532-XXXX', currency_code: 'USD', industry: 'Technology',
  street: '42 Oak Lane', state: 'California', zip_code: '10001',
  latitude: '40.7128', longitude: '-74.0060', date_of_birth: '1990-05-12',
  year: '2024', time: '14:30:00', domain: 'example.com', ipv4: '192.168.1.1',
  color: 'Blue', hex_color: '#6366f1', file_name: 'report.pdf',
  sentence: 'The quick brown…', word: 'synth', slug: 'my-product',
  ssn: '123-45-6789', iban: 'DE89370…', ipv6: '2001:0db8…',
  mac_address: '00:1B:44…', user_agent: 'Mozilla/5.0…', mime_type: 'application/json',
  json: '{"key":"val"}',
};

/* Default ANSI SQL type per generator type */
// ANSI SQL type picker list — values are bare base types (no parentheses)
const SQL_TYPE_LIST = [
  // ── Numeric ──
  { value: 'INTEGER',          label: 'INTEGER',              group: 'Numeric',    hasLength: false },
  { value: 'SMALLINT',         label: 'SMALLINT',             group: 'Numeric',    hasLength: false },
  { value: 'BIGINT',           label: 'BIGINT',               group: 'Numeric',    hasLength: false },
  { value: 'DECIMAL',          label: 'DECIMAL(p,s)',         group: 'Numeric',    hasLength: true, defaultLength: '10,2' },
  { value: 'NUMERIC',          label: 'NUMERIC(p,s)',         group: 'Numeric',    hasLength: true, defaultLength: '10,2' },
  { value: 'FLOAT',            label: 'FLOAT',                group: 'Numeric',    hasLength: false },
  { value: 'REAL',             label: 'REAL',                 group: 'Numeric',    hasLength: false },
  { value: 'DOUBLE PRECISION', label: 'DOUBLE PRECISION',     group: 'Numeric',    hasLength: false },
  // ── Text ──
  { value: 'CHAR',             label: 'CHAR(n)',              group: 'Text',       hasLength: true, defaultLength: '36' },
  { value: 'VARCHAR',          label: 'VARCHAR(n)',           group: 'Text',       hasLength: true, defaultLength: '255' },
  { value: 'NVARCHAR',         label: 'NVARCHAR(n)',          group: 'Text',       hasLength: true, defaultLength: '255' },
  { value: 'TEXT',             label: 'TEXT',                 group: 'Text',       hasLength: false },
  { value: 'CLOB',             label: 'CLOB',                 group: 'Text',       hasLength: false },
  // ── Date & Time ──
  { value: 'DATE',             label: 'DATE',                 group: 'Date & Time', hasLength: false },
  { value: 'TIME',             label: 'TIME',                 group: 'Date & Time', hasLength: false },
  { value: 'TIMESTAMP',        label: 'TIMESTAMP',            group: 'Date & Time', hasLength: false },
  { value: 'TIMESTAMP WITH TIME ZONE', label: 'TIMESTAMP WITH TIME ZONE', group: 'Date & Time', hasLength: false },
  { value: 'INTERVAL',         label: 'INTERVAL',             group: 'Date & Time', hasLength: false },
  // ── Boolean ──
  { value: 'BOOLEAN',          label: 'BOOLEAN',              group: 'Boolean',    hasLength: false },
  { value: 'BIT',              label: 'BIT',                  group: 'Boolean',    hasLength: false },
  // ── Binary ──
  { value: 'BINARY',           label: 'BINARY(n)',            group: 'Binary',     hasLength: true, defaultLength: '16' },
  { value: 'VARBINARY',        label: 'VARBINARY(n)',         group: 'Binary',     hasLength: true, defaultLength: '255' },
  { value: 'BLOB',             label: 'BLOB',                 group: 'Binary',     hasLength: false },
  // ── Special ──
  { value: 'UUID',             label: 'UUID',                 group: 'Special',    hasLength: false },
  { value: 'JSON',             label: 'JSON',                 group: 'Special',    hasLength: false },
  { value: 'JSONB',            label: 'JSONB',                group: 'Special',    hasLength: false },
  { value: 'XML',              label: 'XML',                  group: 'Special',    hasLength: false },
  { value: 'ARRAY',            label: 'ARRAY',                group: 'Special',    hasLength: false },
];

const SQL_SAMPLE_VALUES = {
  'INTEGER': '42, 1000, -7', 'SMALLINT': '1, 32767', 'BIGINT': '9223372036854',
  'DECIMAL': '19.99, 1234.56', 'NUMERIC': '0.75, 99.00',
  'FLOAT': '3.14, 2.71', 'REAL': '1.5, 0.001', 'DOUBLE PRECISION': '1.7976931e+308',
  'CHAR': 'a1b2c3d4…', 'VARCHAR': 'Hello world', 'NVARCHAR': 'John Smith',
  'TEXT': 'Long paragraph…', 'CLOB': 'Very large text…',
  'DATE': '2025-04-20', 'TIME': '14:30:00', 'TIMESTAMP': '2025-04-20 14:30:00',
  'TIMESTAMP WITH TIME ZONE': '2025-04-20T14:30Z', 'INTERVAL': '1 year 2 months',
  'BOOLEAN': 'true, false', 'BIT': '0, 1',
  'BINARY': '0x48656C6C6F', 'VARBINARY': '0x…', 'BLOB': '<binary>',
  'UUID': 'a1b2c3d4-e5f6-7890', 'JSON': '{"key": "val"}',
  'JSONB': '{"k": 1}', 'XML': '<tag>val</tag>', 'ARRAY': '[1, 2, 3]',
};

// Split "VARCHAR(255)" → { base: "VARCHAR", length: "255" }
// Split "DECIMAL(10,2)" → { base: "DECIMAL", length: "10,2" }
// Split "INTEGER"       → { base: "INTEGER", length: "" }
function parseSqlType(sqlType) {
  if (!sqlType) return { base: 'VARCHAR', length: '' };
  const idx = sqlType.indexOf('(');
  if (idx === -1) return { base: sqlType.trim().toUpperCase(), length: '' };
  const last = sqlType.lastIndexOf(')');
  return {
    base: sqlType.slice(0, idx).trim().toUpperCase(),
    length: last > idx ? sqlType.slice(idx + 1, last).trim() : '',
  };
}

function buildSqlType(base, length) {
  return length ? `${base}(${length})` : base;
}

const SQL_TYPES = {
  uuid: 'CHAR(36)', int: 'INTEGER', auto_increment: 'INTEGER',
  string: 'VARCHAR(255)', text: 'TEXT', sentence: 'VARCHAR(500)', word: 'VARCHAR(50)', slug: 'VARCHAR(200)',
  float: 'FLOAT', decimal: 'DECIMAL(10,2)', percentage: 'DECIMAL(5,2)',
  name: 'VARCHAR(100)', first_name: 'VARCHAR(50)', last_name: 'VARCHAR(50)',
  username: 'VARCHAR(50)', email: 'VARCHAR(254)', phone: 'VARCHAR(20)',
  age: 'SMALLINT', gender: 'VARCHAR(10)', job_title: 'VARCHAR(100)', ssn: 'CHAR(11)',
  company_name: 'VARCHAR(200)', product_name: 'VARCHAR(200)', store_name: 'VARCHAR(200)',
  brand_name: 'VARCHAR(100)', department: 'VARCHAR(100)', industry: 'VARCHAR(100)',
  sku: 'VARCHAR(30)', price: 'DECIMAL(10,2)', currency_code: 'CHAR(3)',
  credit_card: 'VARCHAR(19)', iban: 'VARCHAR(34)',
  hospital_name: 'VARCHAR(200)', drug_name: 'VARCHAR(150)', diagnosis: 'VARCHAR(200)', blood_type: 'VARCHAR(5)',
  address: 'VARCHAR(300)', street: 'VARCHAR(200)', city: 'VARCHAR(100)',
  state: 'VARCHAR(100)', country: 'VARCHAR(60)', zip_code: 'VARCHAR(10)',
  latitude: 'DECIMAL(9,6)', longitude: 'DECIMAL(9,6)',
  timestamp: 'TIMESTAMP', date: 'DATE', time: 'TIME', date_of_birth: 'DATE', year: 'SMALLINT',
  url: 'VARCHAR(2048)', domain: 'VARCHAR(253)', ipv4: 'VARCHAR(15)', ipv6: 'VARCHAR(45)',
  mac_address: 'CHAR(17)', user_agent: 'VARCHAR(500)',
  boolean: 'BOOLEAN', color: 'VARCHAR(30)', hex_color: 'CHAR(7)',
  file_name: 'VARCHAR(255)', mime_type: 'VARCHAR(127)', json: 'JSON',
};

function ConfigEditor({ onNavigate }) {
  const { apiFetch, user } = useAuth();
  const [configName, setConfigName] = useState('');
  const [configNameError, setConfigNameError] = useState(false);
  const [entities, setEntities] = useState([
    {
      name: 'customers',
      count: 1000,
      batch_size: 500,
      parallel: true,
      fields: [
        { name: 'id', type: 'uuid', sql_type: 'CHAR(36)', primary_key: true, auto_increment: false },
        { name: 'email', type: 'email', sql_type: 'VARCHAR(254)', primary_key: false, auto_increment: false },
        { name: 'name', type: 'name', sql_type: 'VARCHAR(100)', primary_key: false, auto_increment: false }
      ],
      relationships: []
    }
  ]);
  const [activeEntityIdx, setActiveEntityIdx] = useState(0);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [originalName, setOriginalName] = useState(null);   // tracks which saved config we loaded for editing
  const [showSaveAs, setShowSaveAs] = useState(false);
  const [saveAsName, setSaveAsName] = useState('');
  const [saveAsError, setSaveAsError] = useState('');
  const [showGraph, setShowGraph] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [statsOpen, setStatsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('model');   // 'model' | 'relationships' | 'code-view'
  const [codeFormat, setCodeFormat] = useState('json'); // 'json' | 'dbml' | 'yaml'
  const [jsonDraft, setJsonDraft] = useState(null);     // null = in sync with entities
  const [jsonError, setJsonError] = useState('');
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [typePickerField, setTypePickerField] = useState(null);
  const [sqlPickerOpen, setSqlPickerOpen] = useState(false);
  const [sqlPickerField, setSqlPickerField] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [importPending, setImportPending] = useState(null);  // parsed entities waiting for confirm
  const [importError, setImportError]   = useState('');
  const xlsxInputRef = useRef(null);

  // ── Dirty tracking ──
  const savedSnapshotRef = useRef(null);
  const isDirty = useCallback(() => {
    if (!savedSnapshotRef.current) {
      // No save yet — dirty if user changed from default
      const defaultEntities = [{
        name: 'customers', count: 1000, batch_size: 500, parallel: true,
        fields: [
          { name: 'id', type: 'uuid', sql_type: 'CHAR(36)', primary_key: true, auto_increment: false },
          { name: 'email', type: 'email', sql_type: 'VARCHAR(254)', primary_key: false, auto_increment: false },
          { name: 'name', type: 'name', sql_type: 'VARCHAR(100)', primary_key: false, auto_increment: false }
        ],
        relationships: []
      }];
      return JSON.stringify(entities) !== JSON.stringify(defaultEntities);
    }
    return JSON.stringify(entities) !== savedSnapshotRef.current;
  }, [entities]);

  const dirty = isDirty();
  const [navConfirm, setNavConfirm] = useState(null); // { proceed: fn, cancel: fn }

  // Expose dirty state globally so App.jsx navbar can check it
  useEffect(() => {
    window.__configEditorDirty = dirty;
    window.__configEditorPromptSave = dirty
      ? () => new Promise((resolve) => {
          setNavConfirm({
            proceed: () => { setNavConfirm(null); resolve('discard'); },
            save:    () => { setNavConfirm(null); resolve('save'); },
            cancel:  () => { setNavConfirm(null); resolve('cancel'); },
          });
        })
      : null;
    return () => {
      window.__configEditorDirty = false;
      window.__configEditorPromptSave = null;
    };
  }, [dirty]);

  // Block browser close / refresh when dirty
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  useEffect(() => {
    const currentConfig = localStorage.getItem('current_config');
    if (currentConfig) {
      const config = JSON.parse(currentConfig);
      const ents = config.entities || entities;
      setEntities(ents);
      savedSnapshotRef.current = JSON.stringify(ents);
      localStorage.removeItem('current_config');
    }
    // Migrate any old localStorage configs to backend, then load list
    migrateAndLoad();
  }, []);

  const migrateAndLoad = async () => {
    // Check for old localStorage configs and push them to backend
    const oldSaved = localStorage.getItem('saved_configs');
    if (oldSaved) {
      try {
        const configs = JSON.parse(oldSaved);
        for (const cfg of configs) {
          const ents = cfg.data?.entities || cfg.entities || [];
          if (cfg.name && ents.length > 0) {
            await apiFetch(`${API_BASE}/configs`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: cfg.name, entities: ents })
            }).catch(() => {});
          }
        }
        localStorage.removeItem('saved_configs');
      } catch (e) {
        console.warn('Migration from localStorage failed:', e);
      }
    }
    await fetchConfigsList();
  };

  const fetchConfigsList = async () => {
    try {
      const res = await apiFetch(`${API_BASE}/configs`);
      if (res.ok) {
        const data = await res.json();
        setSavedConfigs(data.configs || []);
      }
    } catch (e) {
      console.error('Failed to load configs:', e);
    }
  };

  const saveConfiguration = async () => {
    if (!configName.trim()) {
      setConfigNameError(true);
      return;
    }
    setConfigNameError(false);

    try {
      const res = await apiFetch(`${API_BASE}/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: configName.trim(), entities })
      });
      if (!res.ok) {
        let errMsg = 'Save failed';
        try {
          const err = await res.json();
          errMsg = err.detail || errMsg;
        } catch {}
        alert(errMsg);
        return;
      }
      setOriginalName(configName.trim());
      savedSnapshotRef.current = JSON.stringify(entities);
      await fetchConfigsList();
    } catch (e) {
      alert(e.message || 'Save failed');
      console.error('Save failed:', e);
    }
  };

  const loadSavedConfig = async (cfg) => {
    // Guard: if there are unsaved changes in the current config, confirm before discarding
    if (dirty) {
      const result = await new Promise((resolve) => {
        setNavConfirm({
          proceed: () => { setNavConfirm(null); resolve('discard'); },
          save:    () => { setNavConfirm(null); resolve('save'); },
          cancel:  () => { setNavConfirm(null); resolve('cancel'); },
        });
      });
      if (result === 'cancel') return;
      if (result === 'save') await saveConfiguration();
    }
    try {
      setLoadError('');
        const res = await apiFetch(`${API_BASE}/configs/${encodeURIComponent(cfg.name)}`); 
        if (!res.ok) {
          if (res.status === 404) {
            setLoadError(`Config "${cfg.name}" not found (may have been deleted or renamed).`);
            return;
          }
          throw new Error(`Failed to load "${cfg.name}"`);
        }
      const data = await res.json();
      setConfigName(data.name);
      setOriginalName(data.name);
      setEntities(data.entities || []);
      savedSnapshotRef.current = JSON.stringify(data.entities || []);
      setDbmlDraft(null); setDbmlError('');
      setActiveEntityIdx(0);
      setShowGraph(false);   // always switch to the editor view, not the graph
      setRelationshipDrafts({});
      setConfigNameError(false);
      setShowSaveAs(false);
      setSaveAsName('');
      setSaveAsError('');
    } catch (e) {
      console.error('Load failed:', e);
      setLoadError(e.message || 'Failed to load config');
    }
  };

  const saveAsNew = async () => {
    const trimmed = saveAsName.trim();
    if (!trimmed) { setSaveAsError('Name is required'); return; }
    // Check if name already exists
    if (savedConfigs.find(c => c.name === trimmed)) { setSaveAsError('Name already exists'); return; }
    try {
      const res = await apiFetch(`${API_BASE}/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmed, entities })
      });
      if (!res.ok) {
        const err = await res.json();
        setSaveAsError(err.detail || 'Save failed');
        return;
      }
      setConfigName(trimmed);
      setOriginalName(trimmed);
      savedSnapshotRef.current = JSON.stringify(entities);
      setShowSaveAs(false);
      setSaveAsName('');
      setSaveAsError('');
      await fetchConfigsList();
    } catch (e) {
      setSaveAsError('Save failed');
    }
  };

  const clearEditor = () => {
    const blank = [{
      name: 'entity_0', count: 1000, batch_size: 500, parallel: true,
      fields: [], relationships: []
    }];
    setConfigName('');
    setOriginalName(null);
    setEntities(blank);
    savedSnapshotRef.current = JSON.stringify(blank);
    setDbmlDraft(null); setDbmlError('');
    setActiveEntityIdx(0);
    setConfigNameError(false);
    setRelationshipDrafts({});
  };

  const deleteSavedConfig = async (name, e) => {
    e.stopPropagation();
    try {
      await apiFetch(`${API_BASE}/configs/${encodeURIComponent(name)}`, { method: 'DELETE' });
      await fetchConfigsList();
      if (originalName === name) {
        setOriginalName(null);
      }
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  /* ── entity / field helpers ── */
  const addEntity = () => {
    const newIdx = entities.length;
    setEntities([...entities, {
      name: `entity_${newIdx}`,
      count: 1000, batch_size: 500, parallel: true,
      fields: [], relationships: []
    }]);
    setActiveEntityIdx(newIdx);
  };

  const updateEntity = (index, updates) => {
    const n = [...entities];
    n[index] = { ...n[index], ...updates };
    setEntities(n);
  };

  const removeEntity = (index) => {
    setEntities(entities.filter((_, i) => i !== index));
    setActiveEntityIdx(Math.max(0, activeEntityIdx - 1));
  };

  const addField = (entityIndex) => {
    const n = [...entities];
    n[entityIndex].fields.push({ name: 'new_field', type: 'string', sql_type: 'VARCHAR(255)', primary_key: false, auto_increment: false });
    setEntities(n);
  };

  const updateField = (entityIndex, fieldIndex, updates) => {
    const n = [...entities];
    n[entityIndex].fields[fieldIndex] = { ...n[entityIndex].fields[fieldIndex], ...updates };
    setEntities(n);
  };

  const removeField = (entityIndex, fieldIndex) => {
    const n = [...entities];
    n[entityIndex].fields.splice(fieldIndex, 1);
    setEntities(n);
  };

  /* ── relationship helpers ── */
  const [relationshipDrafts, setRelationshipDrafts] = useState({});

  const updateRelationshipDraft = (entityIndex, updates) => {
    setRelationshipDrafts(prev => ({
      ...prev,
      [entityIndex]: { ...(prev[entityIndex] || {}), ...updates }
    }));
  };

  const addRelationship = (entityIndex) => {
    const entity = entities[entityIndex];
    const draft = relationshipDrafts[entityIndex] || {};
    const parentEntityName = draft.parent_entity || '';
    if (!parentEntityName) return;
    const parentEntityObj = entities.find(en => en.name === parentEntityName) || { fields: [] };
    const childField = draft.child_field || entity.fields[0]?.name || '';
    const parentField = draft.parent_field || parentEntityObj.fields[0]?.name || '';
    if (!childField || !parentField) return;
    const finalDraft = {
      parent_entity: parentEntityName,
      parent_field: parentField,
      child_field: childField,
      required: draft.required || false,
      cardinality: draft.cardinality || 'many-to-one',
      assignment_strategy: draft.assignment_strategy || 'random',
      assignment_params: draft.assignment_params || {},
      coverage: draft.coverage ?? 1.0,
      min_children: draft.min_children ?? null,
      max_children: draft.max_children ?? null,
    };
    const n = [...entities];
    if (!n[entityIndex].relationships) n[entityIndex].relationships = [];
    n[entityIndex].relationships.push(finalDraft);
    setEntities(n);
    const next = { ...relationshipDrafts };
    delete next[entityIndex];
    setRelationshipDrafts(next);
  };

  const removeRelationship = (entityIndex, relIndex) => {
    const n = [...entities];
    n[entityIndex].relationships = n[entityIndex].relationships.filter((_, i) => i !== relIndex);
    setEntities(n);
  };

  // ── Excel template download ─────────────────────────────────────────────
  const downloadExcelTemplate = () => {
    const hdrs = ['Entity', 'Row Count', 'Field Name', 'Generator Type', 'SQL Type', 'Primary Key', 'Auto Increment'];
    const dataRows = [];
    if (entities.length > 0 && entities.some(e => (e.fields || []).length > 0)) {
      for (const ent of entities) {
        for (const f of (ent.fields || [])) {
          dataRows.push([ent.name, ent.count || 1000, f.name, f.type, f.sql_type || '', f.primary_key ? 'yes' : 'no', f.auto_increment ? 'yes' : 'no']);
        }
      }
    } else {
      // sample rows when editor is empty
      dataRows.push(['customers', 1000, 'id', 'uuid', 'CHAR(36)', 'yes', 'no']);
      dataRows.push(['customers', 1000, 'email', 'email', 'VARCHAR(254)', 'no', 'no']);
      dataRows.push(['customers', 1000, 'full_name', 'name', 'VARCHAR(100)', 'no', 'no']);
      dataRows.push(['orders', 500, 'order_id', 'auto_increment', 'INTEGER', 'yes', 'yes']);
      dataRows.push(['orders', 500, 'amount', 'decimal', 'DECIMAL(10,2)', 'no', 'no']);
      dataRows.push(['orders', 500, 'order_date', 'date', 'DATE', 'no', 'no']);
    }
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.aoa_to_sheet([hdrs, ...dataRows]);
    ws1['!cols'] = [18, 10, 20, 18, 18, 12, 14].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws1, 'Model Template');
    // Reference sheet: all generator types
    const refHdrs = ['Generator Type', 'Group', 'Label'];
    const refRows = FIELD_TYPES.map(t => [t.value, t.group, t.label]);
    const ws2 = XLSX.utils.aoa_to_sheet([refHdrs, ...refRows]);
    ws2['!cols'] = [20, 15, 28].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws2, 'Generator Types');
    // SQL types reference
    const sqlHdrs = ['SQL Type', 'Group', 'Accepts Length?', 'Default Length'];
    const sqlRows = SQL_TYPE_LIST.map(t => [t.value, t.group, t.hasLength ? 'yes' : 'no', t.defaultLength || '']);
    const ws3 = XLSX.utils.aoa_to_sheet([sqlHdrs, ...sqlRows]);
    ws3['!cols'] = [24, 14, 16, 14].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws3, 'SQL Types');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'synthloom_model_template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Excel import parse ──────────────────────────────────────────────────
  const handleExcelFile = (e) => {
    const file = e.target.files?.[0];
    if (xlsxInputRef.current) xlsxInputRef.current.value = '';
    if (!file) return;
    setImportError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (rows.length < 2) { setImportError('The file appears to be empty.'); return; }
        const hdr = rows[0].map(h => String(h).toLowerCase().trim());
        const col = (name) => hdr.indexOf(name);
        const cEnt = col('entity'); const cField = col('field name');
        if (cEnt === -1 || cField === -1) {
          setImportError('Required columns missing: "Entity" and "Field Name" must be present.');
          return;
        }
        const cCount = col('row count'); const cType = col('generator type');
        const cSql = col('sql type'); const cPK = col('primary key'); const cAI = col('auto increment');
        const entityMap = new Map();
        for (let i = 1; i < rows.length; i++) {
          const r = rows[i];
          const entName = String(r[cEnt] ?? '').trim();
          const fieldName = String(r[cField] ?? '').trim();
          if (!entName || !fieldName) continue;
          if (!entityMap.has(entName)) {
            entityMap.set(entName, {
              name: entName,
              count: (cCount !== -1 && parseInt(r[cCount])) || 1000,
              batch_size: 500, parallel: true,
              fields: [], relationships: [],
            });
          }
          const ent = entityMap.get(entName);
          const rawType = cType !== -1 ? String(r[cType] ?? '').trim().toLowerCase() : 'string';
          const sqlVal = cSql !== -1 ? String(r[cSql] ?? '').trim() : '';
          ent.fields.push({
            name: fieldName,
            type: rawType || 'string',
            sql_type: sqlVal || SQL_TYPES[rawType] || 'VARCHAR(255)',
            primary_key: /^(yes|true|1)$/i.test(String(r[cPK] ?? '')),
            auto_increment: /^(yes|true|1)$/i.test(String(r[cAI] ?? '')),
          });
        }
        const result = Array.from(entityMap.values());
        if (result.length === 0) { setImportError('No valid rows found in the file.'); return; }
        setImportPending(result);
      } catch (err) {
        setImportError('Could not parse the file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const confirmImport = () => {
    if (!importPending) return;
    setEntities(importPending);
    setDbmlDraft(null); setDbmlError('');
    setActiveEntityIdx(0);
    setImportPending(null);
    setImportError('');
  };

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const entity = entities[activeEntityIdx] || null;

  // PK validation: at most one primary key per entity
  const pkCount = entity ? entity.fields.filter(f => f.primary_key).length : 0;
  const pkError = pkCount > 1;

  const totalFields = entities.reduce((s, e) => s + (e.fields?.length || 0), 0);
  const totalRels = entities.reduce((s, e) => s + (e.relationships?.length || 0), 0);

  // ── Code-view generators ──
  const buildJson = () => JSON.stringify({ name: configName || 'config', entities }, null, 2);

  const buildYaml = () => {
    const lines = [`name: ${configName || 'config'}`, 'entities:'];
    entities.forEach(e => {
      lines.push(`  - name: ${e.name}`);
      lines.push(`    count: ${e.count || 0}`);
      if (e.fields?.length) {
        lines.push('    fields:');
        e.fields.forEach(f => {
          lines.push(`      - name: ${f.name}`);
          lines.push(`        type: ${f.type}`);
          if (f.sql_type) lines.push(`        sql_type: ${f.sql_type}`);
          if (f.primary_key) lines.push('        primary_key: true');
          if (f.not_null) lines.push('        not_null: true');
          if (f.unique) lines.push('        unique: true');
        });
      }
      if (e.relationships?.length) {
        lines.push('    relationships:');
        e.relationships.forEach(r => {
          lines.push(`      - child_field: ${r.child_field}`);
          lines.push(`        parent_entity: ${r.parent_entity}`);
          lines.push(`        parent_field: ${r.parent_field}`);
          lines.push(`        cardinality: ${r.cardinality || 'many-to-one'}`);
        });
      }
    });
    return lines.join('\n');
  };

  const buildDbml = () => {
    const lines = [];
    entities.forEach(e => {
      lines.push(`Table ${e.name} {`);
      (e.fields || []).forEach(f => {
        const sqlType = f.sql_type || 'varchar(255)';
        const attrs = [];
        if (f.primary_key) attrs.push('pk');
        if (f.auto_increment) attrs.push('increment');
        if (f.not_null) attrs.push('not null');
        if (f.unique) attrs.push('unique');
        const attrStr = attrs.length ? ` [${attrs.join(', ')}]` : '';
        lines.push(`  ${f.name} ${sqlType}${attrStr}`);
      });
      lines.push('}');
      lines.push('');
    });
    entities.forEach(e => {
      (e.relationships || []).forEach(r => {
        lines.push(`Ref: ${e.name}.${r.child_field} > ${r.parent_entity}.${r.parent_field}`);
      });
    });
    return lines.join('\n').trim();
  };

  // --- DBML parser ---
  function parseDbml(dbml) {
    const entities = [];
    const entityMap = {};
    const lines = dbml.split(/\r?\n/);
    let currentEntity = null;
    for (let line of lines) {
      line = line.trim();
      const tableMatch = line.match(/^Table\s+(\w+)\s*\{/);
      if (tableMatch) {
        currentEntity = { name: tableMatch[1], fields: [], relationships: [] };
        entities.push(currentEntity);
        entityMap[currentEntity.name] = currentEntity;
        continue;
      }
      if (line === '}') { currentEntity = null; continue; }
      if (currentEntity && line && !line.startsWith('//')) {
        const fieldMatch = line.match(/^(\w+)\s+([\w()]+)(\s*\[(.+)\])?/);
        if (fieldMatch) {
          const [, fname, ftype, , attrs] = fieldMatch;
          const field = { name: fname, sql_type: ftype };
          if (attrs) {
            attrs.split(',').map(a => a.trim()).forEach(attr => {
              if (attr === 'pk') field.primary_key = true;
              if (attr === 'increment') field.auto_increment = true;
              if (attr === 'not null') field.not_null = true;
              if (attr === 'unique') field.unique = true;
            });
          }
          currentEntity.fields.push(field);
        }
      }
    }
    for (let refLine of dbml.split(/\r?\n/)) {
      refLine = refLine.trim();
      const refMatch = refLine.match(/^Ref:\s*(\w+)\.(\w+)\s*>\s*(\w+)\.(\w+)/);
      if (refMatch) {
        const [, childEnt, childField, parentEnt, parentField] = refMatch;
        const ent = entityMap[childEnt];
        if (ent) {
          ent.relationships.push({ child_field: childField, parent_entity: parentEnt, parent_field: parentField, cardinality: 'many-to-one' });
        }
      }
    }
    return { entities };
  }

  // Infer semantic type from SQL type (reverse lookup of SQL_TYPES)
  function inferTypeFromSql(sqlType) {
    if (!sqlType) return 'string';
    const s = sqlType.toUpperCase();
    if (s === 'CHAR(36)') return 'uuid';
    if (s === 'VARCHAR(254)') return 'email';
    if (s === 'VARCHAR(20)') return 'phone';
    if (s === 'CHAR(11)') return 'ssn';
    if (s === 'CHAR(17)') return 'mac_address';
    if (s === 'CHAR(3)') return 'currency_code';
    if (s === 'CHAR(7)') return 'hex_color';
    if (s === 'BOOLEAN') return 'boolean';
    if (s === 'TIMESTAMP') return 'timestamp';
    if (s === 'TIME') return 'time';
    if (s === 'DATE') return 'date';
    if (s === 'SMALLINT') return 'age';
    if (s === 'FLOAT') return 'float';
    if (s === 'JSON') return 'json';
    if (s === 'TEXT') return 'text';
    if (/^DECIMAL/.test(s)) return 'decimal';
    if (/^(INTEGER|INT|BIGINT|SERIAL)/.test(s)) return 'int';
    if (/^VARCHAR\(5\d{2,}|^VARCHAR\([1-9]\d{3,}/.test(s)) return 'text';
    return 'string';
  }

  // --- DBML live editing state ---
  const [dbmlDraft, setDbmlDraft] = useState(null);
  const [dbmlError, setDbmlError] = useState('');  // draft persists across format/tab switches

  // For read-only formats; JSON uses jsonDraft textarea instead
  const roContent = codeFormat === 'yaml' ? buildYaml() : buildDbml();
  // Keep jsonDraft in sync when entities change externally (model edits)
  // We reset draft whenever entities change so JSON stays live unless user is actively editing
  const liveJson = buildJson();

  return (
    <div className={`ce-layout ${sidebarCollapsed ? 'ce-sidebar-collapsed' : ''}`}>
      {/* ── Sidebar ── */}
      <aside className={`ce-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="ce-sb-header">
          <button
            className="ce-sb-toggle"
            onClick={() => setSidebarCollapsed(v => !v)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              {sidebarCollapsed
                ? <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                : <path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              }
            </svg>
          </button>
          {!sidebarCollapsed && (
            <>
              <div className="sb-section" style={{ flex: 1 }}>Saved Models</div>
              <button className="ce-sb-new-btn" onClick={clearEditor} title="New blank config">
                <SvgNew />
              </button>
            </>
          )}
        </div>
        {!sidebarCollapsed && (
          <>
            {loadError && (
              <div style={{ padding: '8px 12px', color: '#dc2626', fontSize: 12, background: 'rgba(220,38,38,.08)', borderRadius: 6, margin: '4px 8px' }}>
                {loadError}
              </div>
            )}
            {savedConfigs.length === 0 ? ( 
              <div className="ce-sb-empty">No saved configs yet</div> 
            ) : savedConfigs.map((cfg, i) => { 
              // Use a unique key: name + timestamp if available 
              const key = cfg.timestamp ? `${cfg.name}_${cfg.timestamp}` : cfg.name; 
              return ( 
                <div 
                  key={key} 
                  className={`sb-item ${originalName === cfg.name ? 'active' : ''}`} 
                  onClick={() => loadSavedConfig(cfg)} 
                > 
                  <div className="sb-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} /> 
                  <div className="sb-name-block"> 
                    <span className="sb-name">{cfg.name}</span> 
                    <span className="sb-name-hint"> 
                      {originalName === cfg.name ? 'editing' : formatDate(cfg.timestamp)} 
                    </span> 
                  </div> 
                  <span className="sb-count">{cfg.entities || 0}</span> 
                  <div className="ce-sb-del" onClick={(e) => deleteSavedConfig(cfg.name, e)} title="Delete"> 
                    <SvgTrash /> 
                  </div> 
                </div> 
              ); 
            })}
            <div className="sb-footer">
              {savedConfigs.length} config{savedConfigs.length !== 1 ? 's' : ''} saved
            </div>
          </>
        )}
      </aside>

      {/* ── Main ── */}
      <div className="ce-main">
        {/* Config name — top, mandatory */}
        <div className="ce-name-bar">
          <div className="ce-name-field">
            <label className="ce-name-label">
              Model name <span className="ce-required">*</span>
              {originalName && (
                <span className="ce-editing-badge">Editing: {originalName}</span>
              )}
            </label>
            <input
              type="text"
              className={`ce-name-input ${configNameError ? 'error' : ''}`}
              placeholder="e.g. Q1-2025-Config"
              value={configName}
              onChange={(e) => { setConfigName(e.target.value); setConfigNameError(false); }}
            />
            {configNameError && <span className="ce-name-error">Config name is required</span>}
          </div>
          <div className="ce-name-actions">
            <button className={`ov-btn ov-btn-sm ${dirty ? 'ce-save-dirty' : ''}`} onClick={saveConfiguration} title={dirty ? 'Unsaved changes — click to save' : 'Save config'}>
              <span className="ce-save-icon-wrap">
                <SvgSave />
                {dirty && <span className="ce-dirty-badge"><SvgPencil /></span>}
              </span>
              Save
            </button>
            <div className="ce-saveas-wrapper">
              <button className="ov-btn ov-btn-sm" onClick={() => { setShowSaveAs(!showSaveAs); setSaveAsName(''); setSaveAsError(''); }} title="Save as new name">
                <SvgCopy /> Save as
              </button>
              {showSaveAs && (
                <div className="ce-saveas-popover">
                  <div className="ce-saveas-title">Save as new config</div>
                  <input
                    type="text"
                    className={`ce-saveas-input ${saveAsError ? 'error' : ''}`}
                    placeholder="New config name"
                    value={saveAsName}
                    onChange={(e) => { setSaveAsName(e.target.value); setSaveAsError(''); }}
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') saveAsNew(); if (e.key === 'Escape') setShowSaveAs(false); }}
                  />
                  {saveAsError && <span className="ce-saveas-error">{saveAsError}</span>}
                  <div className="ce-saveas-actions">
                    <button className="ov-btn ov-btn-sm" onClick={() => setShowSaveAs(false)}>Cancel</button>
                    <button className="ov-btn ov-btn-primary ov-btn-sm" onClick={saveAsNew}>Save copy</button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Excel template download / import ── */}
            <div className="ce-excel-inline">
              <button
                className="ce-excel-btn ce-excel-btn-download"
                onClick={downloadExcelTemplate}
                title="Download current model as Excel template (.xlsx)"
              >
                <svg width="12" height="12" viewBox="0 0 13 13" fill="none">
                  <path d="M6.5 1v8M3.5 6.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1 10.5h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
                Template
              </button>
              <label
                className="ce-excel-btn ce-excel-btn-import"
                title="Import entities from Excel (.xlsx / .xls)"
              >
                <svg width="12" height="12" viewBox="0 0 13 13" fill="none">
                  <path d="M6.5 9V1M3.5 3.5l3-3 3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M1 10.5h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
                Import .xlsx
                <input
                  ref={xlsxInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleExcelFile}
                />
              </label>
              {importError && (
                <span className="ce-excel-error" title={importError}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="#ef4444" strokeWidth="1.2"/><path d="M6 3.5v3M6 8.5v.5" stroke="#ef4444" strokeWidth="1.2" strokeLinecap="round"/></svg>
                  {importError}
                </span>
              )}
            </div>

            {/* Free user config limit enforcement */}
            {user?.plan === 'free' && savedConfigs.length >= 10 ? (
              <span className="ov-btn ov-btn-primary ov-btn-sm ce-btn-disabled" style={{ opacity: 0.7, cursor: 'not-allowed', position: 'relative' }}
                title="Upgrade to Pro to add more configs."
                tabIndex={0}
                onMouseEnter={e => {
                  const tip = e.currentTarget.querySelector('.ce-tooltip');
                  if (tip) tip.style.opacity = 1;
                }}
                onMouseLeave={e => {
                  const tip = e.currentTarget.querySelector('.ce-tooltip');
                  if (tip) tip.style.opacity = 0;
                }}
              >
                <SvgPlus /> Add config
                <span className="ce-tooltip" style={{
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  pointerEvents: 'none',
                  position: 'absolute',
                  left: '100%',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  background: '#222',
                  color: '#fff',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  maxWidth: '220px',
                  marginLeft: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.13)'
                }}>Upgrade to Pro to add more configs.</span>
              </span>
            ) : (
              <button className="ov-btn ov-btn-primary ov-btn-sm" onClick={addEntity}>
                <SvgPlus /> Add entity
              </button>
            )}
          </div>
        </div>

        {/* ── Top-level tabs: Model / Relationships / Code View ── */}
        <div className="ce-top-tabs">
          <button
            className={`ce-top-tab ${activeTab === 'model' ? 'active' : ''}`}
            onClick={() => setActiveTab('model')}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><rect x="1.5" y="1.5" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.1"/><rect x="7.5" y="1.5" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.1"/><rect x="1.5" y="7.5" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.1"/><rect x="7.5" y="7.5" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.1"/></svg>
            Model
          </button>
          <button
            className={`ce-top-tab ${activeTab === 'relationships' ? 'active' : ''}`}
            onClick={() => setActiveTab('relationships')}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="3" cy="3" r="2" stroke="currentColor" strokeWidth="1.1"/><circle cx="10" cy="3" r="2" stroke="currentColor" strokeWidth="1.1"/><circle cx="6.5" cy="10.5" r="2" stroke="currentColor" strokeWidth="1.1"/><line x1="4.5" y1="4" x2="5.5" y2="8.5" stroke="currentColor" strokeWidth="1"/><line x1="8.5" y1="4" x2="7.5" y2="8.5" stroke="currentColor" strokeWidth="1"/></svg>
            Relationships
          </button>
          <button
            className={`ce-top-tab ${activeTab === 'code-view' ? 'active' : ''}`}
            onClick={() => setActiveTab('code-view')}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M4.5 4L2 6.5l2.5 2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8.5 4L11 6.5 8.5 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 2.5l-2 8" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/></svg>
            Code View
          </button>
        </div>

        {/* ── MODEL tab: entity sub-tabs + editor ── */}
        {activeTab === 'model' && (
          <>
            {/* Entity tabs */}
            <div className="ce-entity-tabs">
              {entities.map((ent, idx) => (
                <button
                  key={idx}
                  className={`ce-etab ${activeEntityIdx === idx ? 'active' : ''}`}
                  onClick={() => setActiveEntityIdx(idx)}
                >
                  <div className="sb-dot" style={{ background: DOT_COLORS[idx % DOT_COLORS.length], width: 6, height: 6 }} />
                  {ent.name}
                </button>
              ))}
              <button className="ce-etab-add" onClick={addEntity}>
                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 1v9M1 5.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                Add entity
              </button>
            </div>

            {/* Entity editor */}
            {entity && (
              <div className="ce-card">
                {/* Fields Section — New Bucket Layout */}
            <div className="ce-top-bar">
              <div className="ce-top-bar-field">
                <label className="ce-top-bar-label">Entity name</label>
                <input type="text" className="ce-top-bar-input" value={entity.name}
                  onChange={(e) => updateEntity(activeEntityIdx, { name: e.target.value })}
                  style={{ width: 120 }} />
              </div>
              <div className="ce-top-bar-field">
                <label className="ce-top-bar-label">Row count</label>
                <input type="number" className="ce-top-bar-input" value={entity.count}
                  onChange={(e) => updateEntity(activeEntityIdx, { count: parseInt(e.target.value) || 0 })}
                  style={{ width: 100 }} />
              </div>
              <div className="ce-top-bar-field">
                <label className="ce-top-bar-label">Batch size</label>
                <input type="number" className="ce-top-bar-input" value={entity.batch_size}
                  onChange={(e) => updateEntity(activeEntityIdx, { batch_size: parseInt(e.target.value) || 0 })}
                  style={{ width: 100 }} />
              </div>
              <div className="ce-top-bar-check">
                <input type="checkbox" checked={entity.parallel}
                  onChange={(e) => updateEntity(activeEntityIdx, { parallel: e.target.checked })}
                  id={`parallel-${activeEntityIdx}`} />
                <label htmlFor={`parallel-${activeEntityIdx}`}>Parallel</label>
              </div>
              <button className="ov-btn ov-btn-primary ov-btn-sm" onClick={() => addField(activeEntityIdx)} style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
                <SvgPlus /> Add field
              </button>
            </div>

            {pkError && (
              <div className="ce-pk-warning">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="#dc2626" strokeWidth="1.4"/><path d="M8 4.5v4" stroke="#dc2626" strokeWidth="1.4" strokeLinecap="round"/><circle cx="8" cy="11" r=".8" fill="#dc2626"/></svg>
                Only one primary key is allowed per entity. Please uncheck extra PK fields.
              </div>
            )}

            <div className="ce-fields-list">
              {entity.fields.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13 }}>No fields yet — click Add field</div>
              ) : entity.fields.map((field, fi) => {
                const isIntLike = ['int','auto_increment','age','year'].includes(field.type) || /^(INTEGER|INT|SMALLINT|BIGINT|SERIAL)/i.test(field.sql_type || '');
                return (
                  <div key={fi} className="ce-field-row">
                    <div className="ce-field-num">{fi + 1}</div>
                    <div className="ce-bucket ce-bucket-schema">
                      <div className="ce-bucket-title">Schema</div>
                      <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                        <div style={{ flex: '1 1 0', minWidth: 0 }}>
                          <div className="ce-input-label">Field name</div>
                          <input className="ce-field-input" value={field.name} style={{ width: '100%', maxWidth: 'none' }}
                            onChange={(e) => updateField(activeEntityIdx, fi, { name: e.target.value })} />
                        </div>
                        <div style={{ flex: '1 1 0', minWidth: 140 }}>
                          <div className="ce-input-label">SQL Type</div>
                          {(() => {
                            const rawSql = field.sql_type || SQL_TYPES[field.type] || 'VARCHAR(255)';
                            const { base, length } = parseSqlType(rawSql);
                            const sqlDef = SQL_TYPE_LIST.find(t => t.value === base);
                            const showLen = sqlDef ? sqlDef.hasLength : length !== '';
                            return (
                              <div style={{ display: 'flex', gap: 4, alignItems: 'stretch' }}>
                                <button
                                  className="ce-field-select"
                                  onClick={() => { setSqlPickerField(fi); setSqlPickerOpen(true); }}
                                  title="Click to change SQL data type"
                                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12, flex: '1 1 0', minWidth: 0, textAlign: 'left' }}
                                >
                                  {base}
                                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: 4, opacity: .5, flexShrink: 0 }}>
                                    <path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                <input
                                  className="ce-field-input"
                                  value={showLen ? length : ''}
                                  onChange={(e) => showLen && updateField(activeEntityIdx, fi, { sql_type: buildSqlType(base, e.target.value) })}
                                  placeholder={showLen ? (sqlDef?.defaultLength || 'n') : ''}
                                  title="Length / scale (e.g. 255 or 10,2)"
                                  readOnly={!showLen}
                                  style={{ width: 56, fontFamily: 'var(--font-mono)', fontSize: 12, textAlign: 'center', maxWidth: 'none', padding: '0 6px', flexShrink: 0, visibility: showLen ? 'visible' : 'hidden' }}
                                />
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="ce-pill-row">
                        <button
                          className={`ce-pill ${field.primary_key ? 'ce-pill-on' : ''}`}
                          onClick={() => {
                            const on = !field.primary_key;
                            if (on) {
                              const n = [...entities];
                              n[activeEntityIdx].fields.forEach((f, idx) => {
                                n[activeEntityIdx].fields[idx] = { ...f, primary_key: false };
                              });
                              n[activeEntityIdx].fields[fi] = { ...n[activeEntityIdx].fields[fi], primary_key: true };
                              setEntities(n);
                            } else {
                              updateField(activeEntityIdx, fi, { primary_key: false });
                            }
                          }}
                        >PK</button>
                        <button
                          className={`ce-pill ${field.auto_increment ? 'ce-pill-on' : ''} ${!isIntLike ? 'ce-pill-disabled' : ''}`}
                          disabled={!isIntLike}
                          onClick={() => updateField(activeEntityIdx, fi, { auto_increment: !field.auto_increment })}
                        >Auto++</button>
                        <button
                          className={`ce-pill ${field.not_null ? 'ce-pill-on' : ''}`}
                          onClick={() => updateField(activeEntityIdx, fi, { not_null: !field.not_null })}
                        >Not null</button>
                        <button
                          className={`ce-pill ${field.unique ? 'ce-pill-on' : ''}`}
                          onClick={() => updateField(activeEntityIdx, fi, { unique: !field.unique })}
                        >Unique</button>
                      </div>
                    </div>
                    <div className="ce-bucket ce-bucket-gen">
                      <div className="ce-bucket-title">Generation</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                          <div style={{ flex: '1 1 0', minWidth: 0 }}>
                            <div className="ce-input-label">Generator</div>
                            <div className="ce-row2">
                              <button
                                className="ce-field-select"
                                onClick={() => { setTypePickerField(fi); setTypePickerOpen(true); }}
                                title="Click to change type"
                                style={{ flex: 1 }}
                              >
                                {FIELD_TYPES.find(ft => ft.value === field.type)?.label || field.type}
                                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: 4, opacity: .5 }}>
                                  <path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button className="ce-del-btn" onClick={() => removeField(activeEntityIdx, fi)} title="Remove field">×</button>
                              <span className="ce-sample-badge" style={{ backgroundColor: '#E6F1FB', color: '#185FA5', flexShrink: 0, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={SAMPLE_VALUES[field.type] || field.type}>
                                {SAMPLE_VALUES[field.type] || field.type}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6 }}>
                            {field.type === 'date' ? (
                              <>
                                <div>
                                  <div className="ce-input-label">Min</div>
                                  <input
                                    className="ce-field-input"
                                    type="date"
                                    placeholder="min date"
                                    style={{ maxWidth: 'none' }}
                                    value={field.min_value ?? ''}
                                    onChange={e => updateField(activeEntityIdx, fi, { min_value: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <div className="ce-input-label">Max</div>
                                  <input
                                    className="ce-field-input"
                                    type="date"
                                    placeholder="max date"
                                    style={{ maxWidth: 'none' }}
                                    value={field.max_value ?? ''}
                                    onChange={e => updateField(activeEntityIdx, fi, { max_value: e.target.value })}
                                  />
                                </div>
                                <div>
                                  <div className="ce-input-label">Format</div>
                                  <input
                                    className="ce-field-input"
                                    placeholder="e.g. YYYY-MM-DD"
                                    style={{ maxWidth: 'none' }}
                                    value={field.format ?? ''}
                                    onChange={e => updateField(activeEntityIdx, fi, { format: e.target.value })}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <div className="ce-input-label">Min</div>
                                  <input className="ce-field-input" placeholder="min" style={{ maxWidth: 'none' }}
                                    value={field.min_value ?? ''}
                                    onChange={e => updateField(activeEntityIdx, fi, { min_value: e.target.value !== '' ? Number(e.target.value) : undefined })} />
                                </div>
                                <div>
                                  <div className="ce-input-label">Max</div>
                                  <input className="ce-field-input" placeholder="max" style={{ maxWidth: 'none' }}
                                    value={field.max_value ?? ''}
                                    onChange={e => updateField(activeEntityIdx, fi, { max_value: e.target.value !== '' ? Number(e.target.value) : undefined })} />
                                </div>
                              </>
                            )}
                            <div>
                              <div className="ce-input-label" style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                Regex
                                <span className="ce-tooltip-wrap">
                                  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{ opacity: .55, cursor: 'default', flexShrink: 0 }}>
                                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/>
                                    <path d="M8 7v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                                    <circle cx="8" cy="4.5" r=".8" fill="currentColor"/>
                                  </svg>
                                  <span className="ce-tooltip">
                                    Pattern to generate matching values.<br/>
                                    <strong>Examples:</strong><br/>
                                    <code>[A-Z]&#123;3&#125;-\d&#123;4&#125;</code> → <em>ABC-1234</em><br/>
                                    <code>\d&#123;3&#125;-\d&#123;2&#125;-\d&#123;4&#125;</code> → <em>123-45-6789</em><br/>
                                    <code>(USD|EUR|GBP)</code> → <em>USD</em><br/>
                                    <code>[a-z]&#123;5,8&#125;@\w+\.com</code> → <em>hello@example.com</em>
                                  </span>
                                </span>
                              </div>
                              <input className="ce-field-input" placeholder="/regex/" style={{ maxWidth: 'none', fontFamily: 'var(--font-mono)', fontSize: 12 }}
                                value={field.regex ?? ''}
                                onChange={e => updateField(activeEntityIdx, fi, { regex: e.target.value })} />
                            </div>
                            <div>
                              <div className="ce-input-label" title="Null probability 0–100%. Only applies when field can be null.">Null %</div>
                              <input className="ce-field-input" placeholder="10" type="number" min="0" max="100" step="1" style={{ maxWidth: 'none' }}
                                value={field.null_rate != null ? Math.round(field.null_rate * 100) : ''}
                                onChange={e => updateField(activeEntityIdx, fi, { null_rate: e.target.value !== '' ? Math.min(1, Math.max(0, Number(e.target.value) / 100)) : undefined })} />
                            </div>
                          </div>
                        </div>
                        <div className="ce-ai-row">
                          <label className="ce-ai-toggle">
                            <input type="checkbox" checked={field.ai_enriched || false}
                              onChange={(e) => updateField(activeEntityIdx, fi, { ai_enriched: e.target.checked })} />
                            <span className="ce-ai-track"><span className="ce-ai-thumb" /></span>
                            <span className="ce-ai-label">AI gen</span>
                          </label>
                          {field.ai_enriched && (
                            <textarea
                              className="ce-ai-prompt"
                              rows={2}
                              placeholder="Describe what to generate, e.g. realistic US city names skewed toward major metros…"
                              value={field.ai_prompt || ''}
                              onChange={e => updateField(activeEntityIdx, fi, { ai_prompt: e.target.value })}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Relationships — Relation Bar */}
            <div className="ce-relation-bar">
              <span className="ce-rel-label">RELATION</span>
              
              <div className="ce-rel-field-group">
                <label className="ce-rel-field-label">Field</label>
                <select className="ce-rel-select"
                  value={relationshipDrafts[activeEntityIdx]?.child_field || ''}
                  onChange={(e) => updateRelationshipDraft(activeEntityIdx, { child_field: e.target.value })}>
                  <option value="">Field…</option>
                  {entity.fields.map((f, fi) => <option key={fi} value={f.name}>{f.name}</option>)}
                </select>
              </div>
              
              <span style={{ color: 'var(--color-text-tertiary)', fontSize: 14, alignSelf: 'flex-end', marginBottom: 4 }}>→</span>
              
              <div className="ce-rel-field-group">
                <label className="ce-rel-field-label">Parent entity</label>
                <select className="ce-rel-select"
                  value={relationshipDrafts[activeEntityIdx]?.parent_entity || ''}
                  onChange={(e) => {
                    const parent = e.target.value;
                    const pe = entities.find(en => en.name === parent);
                    updateRelationshipDraft(activeEntityIdx, {
                      parent_entity: parent,
                      parent_field: pe?.fields[0]?.name || ''
                    });
                  }}>
                  <option value="">Entity…</option>
                  {entities.filter(en => en.name !== entity.name).map((en, i) => <option key={i} value={en.name}>{en.name}</option>)}
                </select>
              </div>
              
              <div className="ce-rel-field-group">
                <label className="ce-rel-field-label">Parent field</label>
                <select className="ce-rel-select"
                  value={relationshipDrafts[activeEntityIdx]?.parent_field || ''}
                  onChange={(e) => updateRelationshipDraft(activeEntityIdx, { parent_field: e.target.value })}>
                  <option value="">Field…</option>
                  {(() => {
                    const pn = relationshipDrafts[activeEntityIdx]?.parent_entity || '';
                    return (entities.find(en => en.name === pn) || { fields: [] }).fields.map((f, fi) =>
                      <option key={fi} value={f.name}>{f.name}</option>
                    );
                  })()}
                </select>
              </div>
              
              <div className="ce-rel-field-group">
                <label className="ce-rel-field-label">Cardinality</label>
                <select className="ce-rel-select"
                  value={relationshipDrafts[activeEntityIdx]?.cardinality || 'many-to-one'}
                  onChange={(e) => updateRelationshipDraft(activeEntityIdx, { cardinality: e.target.value })}>
                  <option value="many-to-one">many-to-one</option>
                  <option value="one-to-many">one-to-many</option>
                  <option value="many-to-many">many-to-many</option>
                </select>
              </div>
              
              <div className="ce-rel-field-group">
                <label className="ce-rel-field-label">Strategy</label>
                <select className="ce-rel-select"
                  value={relationshipDrafts[activeEntityIdx]?.assignment_strategy || 'random'}
                  onChange={e => updateRelationshipDraft(activeEntityIdx, { assignment_strategy: e.target.value })}>
                  <option value="random">Random</option>
                  <option value="round-robin">Round Robin</option>
                  <option value="weighted">Weighted (hot-spot)</option>
                  <option value="clustered">Clustered (Pareto)</option>
                </select>
              </div>

              <div className="ce-rel-field-group">
                <label className="ce-rel-field-label">Coverage %</label>
                <input
                  type="number" min="1" max="100" step="1"
                  className="ce-rel-select"
                  style={{ width: 68 }}
                  title="% of parent records that will have children (e.g. 70 = only 70% of customers get orders)"
                  value={Math.round((relationshipDrafts[activeEntityIdx]?.coverage ?? 1) * 100)}
                  onChange={e => updateRelationshipDraft(activeEntityIdx, { coverage: Math.min(1, Math.max(0.01, Number(e.target.value) / 100)) })}
                />
              </div>

              <div className="ce-rel-field-group">
                <label className="ce-rel-field-label" title="Min children per parent row. Leave blank to use the entity row count instead.">Min / Max children</label>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    type="number" min="0" step="1"
                    className="ce-rel-select"
                    style={{ width: 56 }}
                    placeholder="min"
                    title="Minimum children per parent (blank = use entity count)"
                    value={relationshipDrafts[activeEntityIdx]?.min_children ?? ''}
                    onChange={e => updateRelationshipDraft(activeEntityIdx, { min_children: e.target.value !== '' ? Math.max(0, parseInt(e.target.value)) : null })}
                  />
                  <span style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>&ndash;</span>
                  <input
                    type="number" min="0" step="1"
                    className="ce-rel-select"
                    style={{ width: 56 }}
                    placeholder="max"
                    title="Maximum children per parent (blank = use entity count)"
                    value={relationshipDrafts[activeEntityIdx]?.max_children ?? ''}
                    onChange={e => updateRelationshipDraft(activeEntityIdx, { max_children: e.target.value !== '' ? Math.max(0, parseInt(e.target.value)) : null })}
                  />
                </div>
              </div>

              <div className="ce-toggle-item"style={{ alignSelf: 'flex-end', marginBottom: 4 }}>
                <input type="checkbox"
                  checked={relationshipDrafts[activeEntityIdx]?.required || false}
                  onChange={(e) => updateRelationshipDraft(activeEntityIdx, { required: e.target.checked })} />
                <span>Required</span>
              </div>
              
              <button className="ce-add-rel-btn" onClick={() => addRelationship(activeEntityIdx)}>+ Add</button>
              <button className="ov-btn ov-btn-sm ce-btn-danger" onClick={() => removeEntity(activeEntityIdx)} style={{ marginLeft: 'auto' }}>
                <SvgTrash /> Remove Entity
              </button>
            </div>

            {/* Existing relationships list — editable */}
            {entity.relationships?.length > 0 && (
              <div className="ce-rel-list">
                {entity.relationships.map((r, ri) => (
                  <div key={ri} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', padding: '10px 14px', background: 'var(--color-background-secondary)', border: '0.5px solid var(--color-border-tertiary)', borderRadius: 8, marginBottom: 4 }}>
                    <div className="ce-rel-field-group">
                      <label className="ce-rel-field-label">Field</label>
                      <select className="ce-rel-select"
                        value={r.child_field || ''}
                        onChange={(e) => {
                          const n = [...entities];
                          n[activeEntityIdx].relationships[ri].child_field = e.target.value;
                          setEntities(n);
                        }}>
                        <option value="">Field…</option>
                        {entity.fields.map((f, fi) => <option key={fi} value={f.name}>{f.name}</option>)}
                      </select>
                    </div>
                    <span style={{ color: 'var(--color-text-tertiary)', fontSize: 14, alignSelf: 'flex-end', marginBottom: 4 }}>→</span>
                    <div className="ce-rel-field-group">
                      <label className="ce-rel-field-label">Parent entity</label>
                      <select className="ce-rel-select"
                        value={r.parent_entity || ''}
                        onChange={(e) => {
                          const parent = e.target.value;
                          const n = [...entities];
                          const pe = entities.find(en => en.name === parent);
                          n[activeEntityIdx].relationships[ri].parent_entity = parent;
                          n[activeEntityIdx].relationships[ri].parent_field = pe?.fields[0]?.name || '';
                          setEntities(n);
                        }}>
                        <option value="">Entity…</option>
                        {entities.filter(en => en.name !== entity.name).map((en, i) => <option key={i} value={en.name}>{en.name}</option>)}
                      </select>
                    </div>
                    <div className="ce-rel-field-group">
                      <label className="ce-rel-field-label">Parent field</label>
                      <select className="ce-rel-select"
                        value={r.parent_field || ''}
                        onChange={(e) => {
                          const n = [...entities];
                          n[activeEntityIdx].relationships[ri].parent_field = e.target.value;
                          setEntities(n);
                        }}>
                        <option value="">Field…</option>
                        {(() => {
                          const pn = r.parent_entity || '';
                          return (entities.find(en => en.name === pn) || { fields: [] }).fields.map((f, fi) =>
                            <option key={fi} value={f.name}>{f.name}</option>
                          );
                        })()}
                      </select>
                    </div>
                    <div className="ce-rel-field-group">
                      <label className="ce-rel-field-label">Cardinality</label>
                      <select className="ce-rel-select"
                        value={r.cardinality || 'many-to-one'}
                        onChange={(e) => {
                          const n = [...entities];
                          n[activeEntityIdx].relationships[ri].cardinality = e.target.value;
                          setEntities(n);
                        }}>
                        <option value="many-to-one">many-to-one</option>
                        <option value="one-to-many">one-to-many</option>
                        <option value="many-to-many">many-to-many</option>
                      </select>
                    </div>
                    <div className="ce-rel-field-group">
                      <label className="ce-rel-field-label">Strategy</label>
                      <select className="ce-rel-select"
                        value={r.assignment_strategy || 'random'}
                        onChange={(e) => {
                          const n = [...entities];
                          n[activeEntityIdx].relationships[ri].assignment_strategy = e.target.value;
                          setEntities(n);
                        }}>
                        <option value="random">Random</option>
                        <option value="round-robin">Round Robin</option>
                        <option value="weighted">Weighted (hot-spot)</option>
                        <option value="clustered">Clustered (Pareto)</option>
                      </select>
                    </div>
                    <div className="ce-rel-field-group">
                      <label className="ce-rel-field-label">Coverage %</label>
                      <input
                        type="number" min="1" max="100" step="1"
                        className="ce-rel-select"
                        style={{ width: 68 }}
                        value={Math.round((r.coverage ?? 1) * 100)}
                        onChange={(e) => {
                          const n = [...entities];
                          n[activeEntityIdx].relationships[ri].coverage = Math.min(1, Math.max(0.01, Number(e.target.value) / 100));
                          setEntities(n);
                        }}
                      />
                    </div>
                    <div className="ce-rel-field-group">
                      <label className="ce-rel-field-label">Min / Max children</label>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input
                          type="number" min="0" step="1"
                          className="ce-rel-select"
                          style={{ width: 56 }}
                          placeholder="min"
                          value={r.min_children ?? ''}
                          onChange={(e) => {
                            const n = [...entities];
                            n[activeEntityIdx].relationships[ri].min_children = e.target.value !== '' ? Math.max(0, parseInt(e.target.value)) : null;
                            setEntities(n);
                          }}
                        />
                        <span style={{ color: 'var(--color-text-tertiary)', fontSize: 12 }}>&ndash;</span>
                        <input
                          type="number" min="0" step="1"
                          className="ce-rel-select"
                          style={{ width: 56 }}
                          placeholder="max"
                          value={r.max_children ?? ''}
                          onChange={(e) => {
                            const n = [...entities];
                            n[activeEntityIdx].relationships[ri].max_children = e.target.value !== '' ? Math.max(0, parseInt(e.target.value)) : null;
                            setEntities(n);
                          }}
                        />
                      </div>
                    </div>
                    <button className="ce-rel-chip-del" onClick={() => removeRelationship(activeEntityIdx, ri)} title="Remove relationship" style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
            )}
          </>
        )}

        {/* RELATIONSHIPS tab */}
        {activeTab === 'relationships' && (
          <div className="ce-card">
            <div className="ce-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Entity Relationship Diagram</span>
              <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{entities.length} entities · {totalRels} relationships</span>
            </div>
            <RelationshipGraph
              entities={entities}
              colors={DOT_COLORS}
              title={configName || 'Config'}
              onAddRelationship={({ childEntity, childField, parentEntity, parentField, cardinality, required }) => {
                const childIdx = entities.findIndex(en => en.name === childEntity);
                if (childIdx < 0) return;
                const n = entities.map((e, i) => i === childIdx ? { ...e, relationships: [...(e.relationships || [])] } : e);
                const dup = n[childIdx].relationships.some(r =>
                  r.child_field === childField && r.parent_entity === parentEntity && r.parent_field === parentField
                );
                if (dup) return;
                n[childIdx].relationships.push({
                  parent_entity: parentEntity,
                  parent_field: parentField,
                  child_field: childField,
                  required: required || false,
                  cardinality: cardinality || 'many-to-one',
                });
                setEntities(n);
              }}
              onDeleteRelationship={({ entityName, relIndex }) => {
                const idx = entities.findIndex(en => en.name === entityName);
                if (idx < 0) return;
                const n = entities.map((e, i) => {
                  if (i !== idx) return e;
                  return { ...e, relationships: (e.relationships || []).filter((_, ri) => ri !== relIndex) };
                });
                setEntities(n);
              }}
              onTogglePrimaryKey={({ entityName, fieldName, isPK }) => {
                const eIdx = entities.findIndex(en => en.name === entityName);
                if (eIdx < 0) return;
                const n = entities.map((e, i) => {
                  if (i !== eIdx) return e;
                  return {
                    ...e,
                    fields: e.fields.map(f => {
                      if (f.name === fieldName) return { ...f, primary_key: isPK };
                      if (isPK) return { ...f, primary_key: false };
                      return f;
                    })
                  };
                });
                setEntities(n);
              }}
              onReorderFields={({ entityName, fields }) => {
                const eIdx = entities.findIndex(en => en.name === entityName);
                if (eIdx < 0) return;
                const n = entities.map((e, i) => {
                  if (i !== eIdx) return e;
                  return { ...e, fields };
                });
                setEntities(n);
              }}
            />
          </div>
        )}

        {/* CODE VIEW tab */}
        {activeTab === 'code-view' && (
          <div className="ce-card ce-code-card">
            <div className="ce-code-header">
              <div className="ce-code-title">Code View</div>
              <div className="ce-code-formats">
                {['json', 'dbml', 'yaml'].map(f => (
                  <button
                    key={f}
                    className={`ce-code-fmt-btn ${codeFormat === f ? 'active' : ''}`}
                    onClick={() => { setCodeFormat(f); setJsonError(''); }}
                  >
                    {f.toUpperCase()}
                    {(f === 'json' || f === 'dbml') && <span className="ce-code-fmt-badge">editable</span>}
                  </button>
                ))}
              </div>
              <button
                className="ce-code-copy-btn"
                onClick={() => navigator.clipboard.writeText(codeFormat === 'json' ? (jsonDraft ?? liveJson) : codeFormat === 'dbml' ? (dbmlDraft ?? buildDbml()) : roContent)}
                title="Copy to clipboard"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><rect x="4" y="4" width="7" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.1"/><path d="M3 8H2a1 1 0 01-1-1V2a1 1 0 011-1h5a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/></svg>
                Copy
              </button>
            </div>
            <div className="ce-code-desc">
              {codeFormat === 'json' && (
                <span>
                  JSON — editable. Changes here update the model live.
                  {jsonError && <span className="ce-json-error"> ⚠ {jsonError}</span>}
                </span>
              )}
              {codeFormat === 'dbml' && (
                <span>
                  DBML — editable. Changes here update the model live. Paste into dbdiagram.io to visualise.
                  {dbmlError && <span className="ce-json-error"> ⚠ {dbmlError}</span>}
                </span>
              )}
              {codeFormat === 'yaml' && 'YAML — read-only config export. Use for documentation or CI pipeline configs.'}
            </div>
            {codeFormat === 'json' ? (
              <textarea
                className={`ce-code-block ce-code-editable${jsonError ? ' ce-code-error' : ''}`}
                value={jsonDraft ?? liveJson}
                rows={(jsonDraft ?? liveJson).split('\n').length + 1}
                spellCheck={false}
                onChange={(e) => {
                  const val = e.target.value;
                  setJsonDraft(val);
                  try {
                    const parsed = JSON.parse(val);
                    if (parsed.entities && Array.isArray(parsed.entities)) {
                      setEntities(parsed.entities);
                      if (parsed.name) setConfigName(parsed.name);
                      setJsonError('');
                    } else {
                      setJsonError('Missing "entities" array');
                    }
                  } catch (err) {
                    setJsonError(err.message);
                  }
                }}
                onFocus={() => { if (jsonDraft === null) setJsonDraft(liveJson); }}
                onBlur={() => { if (!jsonError) setJsonDraft(null); }}
              />
            ) : codeFormat === 'dbml' ? (
              <textarea
                className={`ce-code-block ce-code-editable${dbmlError ? ' ce-code-error' : ''}`}
                value={dbmlDraft ?? buildDbml()}
                rows={(dbmlDraft ?? buildDbml()).split('\n').length + 1}
                spellCheck={false}
                onChange={e => {
                  setDbmlDraft(e.target.value);
                  try {
                    const parsed = parseDbml(e.target.value);
                    if (parsed.entities && Array.isArray(parsed.entities)) {
                      setEntities(prev => {
                        return parsed.entities.map(parsedEnt => {
                          const existing = prev.find(e => e.name === parsedEnt.name);
                          const mergedFields = parsedEnt.fields.map(pf => {
                            const ef = existing?.fields?.find(f => f.name === pf.name);
                            if (ef) {
                              return { ...ef, sql_type: pf.sql_type, primary_key: pf.primary_key ?? ef.primary_key, auto_increment: pf.auto_increment ?? ef.auto_increment, not_null: pf.not_null ?? ef.not_null, unique: pf.unique ?? ef.unique };
                            }
                            return { ...pf, type: inferTypeFromSql(pf.sql_type), auto_increment: pf.auto_increment ?? false };
                          });
                          if (existing) {
                            return { ...existing, fields: mergedFields, relationships: parsedEnt.relationships };
                          }
                          // Brand-new entity from DBML
                          return { name: parsedEnt.name, count: 1000, batch_size: 500, parallel: true, fields: mergedFields, relationships: parsedEnt.relationships };
                        });
                      });
                      setDbmlError('');
                    } else { setDbmlError('Missing entities'); }
                  } catch (err) { setDbmlError(err.message); }
                }}
                onFocus={() => { if (dbmlDraft === null) setDbmlDraft(buildDbml()); }}
              />
            ) : (
              <pre className="ce-code-block"><code>{roContent}</code></pre>
            )}
          </div>
        )}
      </div>

      {/* Import confirm dialog */}
      {importPending && (
        <div className="ce-confirm-overlay" onClick={() => setImportPending(null)}>
          <div className="ce-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="ce-confirm-icon">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <rect x="3" y="3" width="22" height="22" rx="4" stroke="#22c55e" strokeWidth="2"/>
                <path d="M8 14l4 4 8-8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="ce-confirm-title">Import {importPending.length} {importPending.length === 1 ? 'entity' : 'entities'}?</h3>
            <p className="ce-confirm-msg">
              {importPending.map(e => `${e.name} (${e.fields.length} fields)`).join(' · ')}
              <br/>This will <strong>replace</strong> the current model in the editor.
            </p>
            <div className="ce-confirm-actions">
              <button className="ov-btn ov-btn-sm" onClick={() => setImportPending(null)}>Cancel</button>
              <button className="ov-btn ov-btn-primary ov-btn-sm" onClick={confirmImport}>Import</button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved changes confirmation dialog */}
      {navConfirm && (
        <div className="ce-confirm-overlay" onClick={navConfirm.cancel}>
          <div className="ce-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="ce-confirm-icon">
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                <circle cx="14" cy="14" r="12" stroke="#f59e0b" strokeWidth="2"/>
                <path d="M14 8v7" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"/>
                <circle cx="14" cy="19.5" r="1.2" fill="#f59e0b"/>
              </svg>
            </div>
            <h3 className="ce-confirm-title">Unsaved Changes</h3>
            <p className="ce-confirm-msg">You have unsaved changes in your config. Do you want to save before leaving?</p>
            <div className="ce-confirm-actions">
              <button className="ov-btn ov-btn-sm" onClick={navConfirm.proceed}>Discard</button>
              <button className="ov-btn ov-btn-sm" onClick={navConfirm.cancel}>Cancel</button>
              <button className="ov-btn ov-btn-primary ov-btn-sm" onClick={() => {
                navConfirm.save();
              }}>Save & Load</button>
            </div>
          </div>
        </div>
      )}

      {/* Generator Type Picker Modal */}
      <FieldTypePicker
        open={typePickerOpen}
        fieldTypes={FIELD_TYPES}
        sampleValues={SAMPLE_VALUES}
        currentValue={typePickerField !== null ? entity?.fields[typePickerField]?.type : ''}
        onSelect={(value) => {
          if (typePickerField !== null) {
            const updates = { type: value, sql_type: SQL_TYPES[value] || 'VARCHAR(255)' };
            const isIntLike = ['int','auto_increment','age','year'].includes(value);
            if (!isIntLike) updates.auto_increment = false;
            updateField(activeEntityIdx, typePickerField, updates);
          }
        }}
        onClose={() => { setTypePickerOpen(false); setTypePickerField(null); }}
      />

      {/* SQL Data Type Picker Modal */}
      <FieldTypePicker
        open={sqlPickerOpen}
        fieldTypes={SQL_TYPE_LIST}
        sampleValues={SQL_SAMPLE_VALUES}
        currentValue={sqlPickerField !== null ? parseSqlType(entity?.fields[sqlPickerField]?.sql_type || SQL_TYPES[entity?.fields[sqlPickerField]?.type] || 'VARCHAR(255)').base : ''}
        onSelect={(value) => {
          if (sqlPickerField !== null) {
            const def = SQL_TYPE_LIST.find(t => t.value === value);
            const curSql = entity?.fields[sqlPickerField]?.sql_type || '';
            const { length: existingLength } = parseSqlType(curSql);
            const newLength = def?.hasLength ? (existingLength || def.defaultLength || '') : '';
            updateField(activeEntityIdx, sqlPickerField, { sql_type: buildSqlType(value, newLength) });
          }
        }}
        onClose={() => { setSqlPickerOpen(false); setSqlPickerField(null); }}
      />
      {/* ── Right stats panel ── */}
      <aside className={`ce-stats-panel ${statsOpen ? 'open' : 'collapsed'}`}>
        <button
          className="ce-stats-toggle"
          onClick={() => setStatsOpen(v => !v)}
          title={statsOpen ? 'Hide config summary' : 'Show config summary'}
        >
          {statsOpen ? (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M9 3L5 7l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
          {!statsOpen && <span className="ce-stats-toggle-label">Summary</span>}
        </button>

        {statsOpen && (
          <div className="ce-stats-body">
            <div className="ce-stats-heading">Config Summary</div>

            <div className="ce-stats-item">
              <div className="ce-stats-label">Entities</div>
              <div className="ce-stats-value">{entities.length}</div>
              <div className="ce-stats-sub">{entities.map(e => e.name).join(' · ') || '—'}</div>
            </div>

            <div className="ce-stats-divider" />

            <div className="ce-stats-item">
              <div className="ce-stats-label">Total fields</div>
              <div className="ce-stats-value">{totalFields}</div>
              <div className="ce-stats-bar"><div className="ce-stats-bar-fill" style={{ width: `${Math.min(100, totalFields * 3.5)}%` }} /></div>
            </div>

            <div className="ce-stats-divider" />

            <div className="ce-stats-item">
              <div className="ce-stats-label">Relationships</div>
              <div className="ce-stats-value">{totalRels}</div>
              <div className="ce-stats-sub">{totalRels === 0 ? 'none defined' : `across ${entities.filter(e => e.relationships?.length > 0).length} entities`}</div>
            </div>

            <div className="ce-stats-divider" />

            <div className="ce-stats-item">
              <div className="ce-stats-label">Total rows</div>
              <div className="ce-stats-value">{entities.reduce((s, e) => s + (e.count || 0), 0).toLocaleString()}</div>
              <div className="ce-stats-sub">estimated output size</div>
            </div>

            {entities.length > 0 && (
              <>
                <div className="ce-stats-divider" />
                <div className="ce-stats-label" style={{ marginBottom: 6 }}>Per entity</div>
                {entities.map((e, i) => (
                  <div key={i} className="ce-stats-entity-row">
                    <div className="sb-dot" style={{ background: DOT_COLORS[i % DOT_COLORS.length], width: 6, height: 6, borderRadius: '50%', flexShrink: 0 }} />
                    <span className="ce-stats-entity-name">{e.name}</span>
                    <span className="ce-stats-entity-count">{e.fields?.length || 0} fields</span>
                    <span className="ce-stats-entity-rows">{(e.count || 0).toLocaleString()} rows</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

export default ConfigEditor;